<template>
  <q-select
    v-bind="$attrs"
    :model-value="selectedRecordIds"
    class="col-12 q-mt-md"
    :label="humanModelName"
    :options="modelSearchResults"
    map-options
    emit-value
    multiple
    use-chips
    clearable
    use-input
    input-debounce="500"
    :bg-color="disable ? undefined : 'amber-1'"
    :label-color="disable ? undefined : 'amber-9'"
    :loading="loading"
    :disable="disable"
    data-test-id="model-search-select"
    :rules="[conditionallyRequireValueRule]"
    @update:model-value="handleInput"
    @filter="handleFilter"
  >
    <template
      v-if="!disable"
      #append
    >
      <q-icon
        name="search"
        color="amber-9"
      />
    </template>
    <template #before-options>
      <q-item-label header>
        Found {{ modelSearchResults.length }} recent records
      </q-item-label>
    </template>
    <template #no-option>
      <q-item>
        <q-item-section class="text-italic text-grey">
          No results
        </q-item-section>
      </q-item>
    </template>
  </q-select>
</template>

<script>
import { TYPE_NAME_TO_MODEL_MAP } from 'src/models/model'

/**
 * A generic, searchable multi-select for any searchable Model. Its values are arrays of model record IDs
 * and inputs emit updated arrays of record IDs. A `model` name or class must be provided.
 */
export default {
  name: 'ModelSearchSelect',
  props: {
    /** This is expected to be an array of numeric record IDs */
    modelValue: {
      type: Array,
      required: true
    },
    model: {
      type: [String, Function],
      required: true
    },
    disable: {
      type: Boolean,
      default: false
    },
    requireValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      modelCollection: null,
      modelSearchTerm: '',
      selectedRecordIds: []
    }
  },
  computed: {
    modelClass () {
      if (_.isString(this.model)) {
        return TYPE_NAME_TO_MODEL_MAP[this.model]
      } else {
        return this.model
      }
    },
    modelSearchResults () {
      if (_.isEmpty(this.modelCollection?.data)) return []

      return _(this.modelCollection.data)
        .uniqBy('id')
        .map((record) => ({ label: `${record[this.recordIdentifierField]} (ID: ${record.id})`, value: record.id }))
        .value()
    },
    humanModelName () {
      if (this.modelClass) {
        return this.modelClass.humanTypeName
      } else if (_.isFunction(this.model) && this.model.typeName) {
        return this.model.typeName
      } else if (_.isString(this.model)) {
        return this.model
      } else {
        return 'Unknown Model'
      }
    },
    recordIdentifierField () {
      if (!this.modelClass) return 'id'

      const identifierFieldsInOrderOfPreference = ['name', 'displayName', 'title', 'label', 'firstName', 'nameForOrder', 'id']
      return _.find(identifierFieldsInOrderOfPreference, (fieldName) => _.has(this.modelClass.fields, fieldName))
    },
    loading () {
      return this.modelCollection?.loading || false
    }
  },
  watch: {
    modelValue: {
      handler () {
        // Need to spread because observable arrays were not meeting equality checks
        if (!_.isEqual([...this.modelValue], [...this.selectedRecordIds])) {
          this.selectedRecordIds = this.modelValue
        }
      },
      immediate: true
    },
    modelClass: {
      handler (_newModelClass, oldModelClass) {
        if (oldModelClass) {
          oldModelClass.destroy()
        }
        this.modelCollection = this.initializeModelCollection()
        this.fetchSearchResults()
      },
      immediate: true
    },
    modelSearchTerm (val) {
      if (!this.modelCollection) return

      this.fetchSearchResults()
    },
    selectedRecordIds () {
      this.$emit('update:model-value', this.selectedRecordIds)
    }
  },
  created () {
    if (!_.isEmpty(this.modelValue) && this.modelClass) {
      this.populateCollectionWithInitiallySelectedRecords()
    }
  },
  unmounted () {
    if (this.modelCollection) {
      this.modelCollection.destroy()
    }
  },
  methods: {
    initializeModelCollection () {
      if (!this.modelClass) return null

      const options = {
        fields: _(['id', this.recordIdentifierField]).compact().uniq().value(),
        queryName: this.modelClass.collectionVariableName,
        key: this.modelClass.collectionVariableName,
        isCollection: true
      }
      // eslint-disable-next-line new-cap
      return new this.modelClass({ options })
    },
    populateCollectionWithInitiallySelectedRecords () {
      _.each(this.modelValue, async (id) => {
        // eslint-disable-next-line new-cap
        const model = new this.modelClass({ id, fields: _(['id', this.recordIdentifierField]).compact().uniq().value() })
        await model.fetch()
        this.modelCollection.add(model)
      })
    },
    async handleInput (selectedModelIds) {
      selectedModelIds = _(selectedModelIds).map(_.toNumber).compact().value()
      this.selectedRecordIds = selectedModelIds
    },
    async handleFilter (val, update, _abort) {
      update(() => {
        this.modelSearchTerm = val
      })
    },
    async fetchSearchResults () {
      if (this.modelCollection) {
        this.modelCollection.options.variables = {
          searchString: this.modelSearchTerm,
          limit: 15,
          orderBy: 'id',
          descending: true
        }
        await this.modelCollection.fetch()
      }
    },
    conditionallyRequireValueRule () {
      if (this.requireValue && !this.disable && _.isEmpty(this.selectedRecordIds)) {
        return `Must select at least one ${this.humanModelName}`
      } else {
        return true
      }
    }
  }
}
</script>
